package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.service.LoginService
import org.example.project.service.PasswordChangeApi
import org.example.project.service.PasswordPolicy
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.PasswordRequirementHint
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun PasswordChangeOverlay(
    art: String,
    onClose: () -> Unit,
    onLogout: (() -> Unit)? = null
) {
    val scope = rememberCoroutineScope()
    val currentUser = LoginService.getUser()
    val isDriver = art.equals("driver", ignoreCase = true)
    val desktopTexts = mapOf(
        "driver_change_password" to "Passwort ändern",
        "driver_old_password" to "Altes Passwort",
        "driver_new_password" to "Neues Passwort",
        "driver_password_requirement" to PasswordPolicy.REQUIREMENT_TEXT,
        "driver_repeat_new_password" to "Neues Passwort wiederholen",
        "driver_error_old_password_wrong" to "Bitte das alte Passwort eingeben.",
        "driver_error_new_passwords_not_identical" to "Passwörter stimmen nicht überein.",
        "driver_password_save_failed" to "Passwort konnte nicht geändert werden.",
        "driver_changing_password" to "Wird geändert...",
        "driver_change_password_action" to "Passwort ändern",
        "mobile_logout" to "Abmelden",
        "abbrechen" to "Abbrechen"
    )
    val t: (String) -> String = { key ->
        if (isDriver) LocalizationManager.get(key) else desktopTexts.getValue(key)
    }
    var oldPassword by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var repeatPassword by remember { mutableStateOf("") }
    var errorText by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }

    if (currentUser == null) return

    fun close() {
        if (!saving) onClose()
    }

    fun submit() {
        if (saving) return
        when {
            oldPassword.isBlank() -> {
                errorText = t("driver_error_old_password_wrong")
                return
            }
            PasswordPolicy.error(newPassword) != null -> {
                errorText = t("driver_password_requirement")
                return
            }
            newPassword != repeatPassword -> {
                errorText = t("driver_error_new_passwords_not_identical")
                return
            }
        }
        saving = true
        errorText = null
        scope.launch {
            try {
                val updated = PasswordChangeApi.changeCurrentLoginPassword(newPassword, oldPassword)
                    ?: throw IllegalStateException("password_change_failed")
                LoginService.updateUser { updated }
                onClose()
            } catch (_: Throwable) {
                errorText = t("driver_password_save_failed")
            } finally {
                saving = false
            }
        }
    }

    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            backgroundColor(rgba(0, 0, 0, 0.72))
            property("z-index", "120000")
            padding(20.px)
            boxSizing("border-box")
        }
    }) {
        Div({
            style {
                width(100.percent)
                maxWidth(480.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
                padding(28.px)
                boxSizing("border-box")
                property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
                property("border", "1px solid rgba(255,255,255,0.14)")
                borderRadius(20.px)
                property("box-shadow", "0 24px 60px rgba(0,0,0,0.55)")
            }
        }) {
            H2({
                style {
                    margin(0.px)
                    color(Color.white)
                    fontSize(24.px)
                }
            }) { Text(t("driver_change_password")) }

            @Composable
            fun pwInput(value: String, hint: String, onValue: (String) -> Unit) {
                Input(InputType.Password, attrs = {
                    placeholder(hint)
                    value(value)
                    onInput {
                        onValue(it.value)
                        errorText = null
                    }
                    style {
                        width(100.percent)
                        height(46.px)
                        padding(0.px, 14.px)
                        boxSizing("border-box")
                        color(Color.white)
                        backgroundColor(Color("rgba(255,255,255,0.06)"))
                        property("border", "1px solid rgba(255,255,255,0.16)")
                        borderRadius(12.px)
                    }
                })
            }

            pwInput(oldPassword, t("driver_old_password")) { oldPassword = it }
            pwInput(newPassword, t("driver_new_password")) { newPassword = it }
            PasswordRequirementHint(newPassword, t("driver_password_requirement"))
            pwInput(repeatPassword, t("driver_repeat_new_password")) { repeatPassword = it }

            if (!errorText.isNullOrBlank()) {
                Div({
                    style {
                        color(Color("#ff9e9e"))
                        fontSize(14.px)
                    }
                }) { Text(errorText!!) }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    gap(10.px)
                    flexWrap(FlexWrap.Wrap)
                    marginTop(8.px)
                }
            }) {
                if (onLogout != null) {
                    LumaticaButton(
                        label = t("mobile_logout"),
                        onClick = { if (!saving) onLogout() },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = !saving,
                        heightPx = 42
                    )
                }
                LumaticaButton(
                    label = t("abbrechen"),
                    onClick = { close() },
                    variant = LumaticaButtonVariant.Standard,
                    enabled = !saving,
                    heightPx = 42
                )
                LumaticaButton(
                    label = if (saving) t("driver_changing_password") else t("driver_change_password_action"),
                    onClick = { submit() },
                    variant = LumaticaButtonVariant.Action,
                    enabled = !saving,
                    heightPx = 42
                )
            }
        }
    }
}


